<template>
  <section class="page-wrap">
    <UiPageHero
      eyebrow="Bảo trì bảo dưỡng"
      title="Lịch sử bảo dưỡng"
      description="Theo dõi lịch bảo dưỡng thiết bị, chi phí và trạng thái xử lý."
    >
      <template #actions>
        <UiButton variant="white" icon="add" @click="openCreateModal">
          Tạo lịch
        </UiButton>
      </template>
    </UiPageHero>

    <div class="panel-pad mb-4">
      <div
        class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between"
      >
        <UiInput
          v-model="query"
          class="w-full lg:max-w-md"
          placeholder="Tìm thiết bị, mã, khoa/phòng, ghi chú..."
        />

        <div
          v-if="canManageConfig"
          class="flex flex-col gap-2 border-slate-200 pt-3 text-left lg:min-w-[360px] lg:border-l lg:pl-4 lg:pt-0"
        >
          <div class="flex items-end justify-between gap-3">
            <div>
              <p class="text-[11px] font-bold uppercase text-slate-500">
                Cấu hình bảo dưỡng
              </p>
              <p class="mt-1 text-sm font-semibold text-slate-700">
                Thời gian mặc định:
                <span class="text-xl font-black text-ink">
                  {{
                    configLoading
                      ? "..."
                      : currentConfig?.thoiGianBaoDuong ?? "-"
                  }}
                </span>
              </p>
            </div>
            <UiButton
              v-if="currentConfig"
              variant="secondary"
              icon="edit"
              @click="openUpdateConfigModal"
            >
              Cập nhật
            </UiButton>
            <UiButton
              v-else
              icon="add"
              :disabled="configLoading"
              @click="openCreateConfigModal"
            >
              Tạo cấu hình
            </UiButton>
          </div>
        </div>
      </div>
    </div>

    <div class="table-wrap" :class="{ 'is-loading': loading }">
      <div v-if="loading" class="table-loading-overlay">
        <span class="table-spinner"></span>
        <span>Đang tải dữ liệu...</span>
      </div>

      <table class="data-table min-w-[1500px] w-full">
        <thead>
          <tr>
            <th>STT</th>
            <th>Tên thiết bị</th>
            <th>Mã thiết bị</th>
            <th>Khoa/phòng hiện tại</th>
            <th>Thời gian bảo dưỡng</th>
            <th>Thời gian bảo dưỡng tiếp theo</th>
            <th>Số tiền</th>
            <th>Trạng thái</th>
            <th>Ghi chú</th>
            <th>Thao tác</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          <tr
            v-for="(item, index) in paginatedRows"
            :key="item.id || `${item.idThietBi}-${index}`"
            class="hover:bg-slate-50"
          >
            <td>{{ rowIndex(index) }}</td>
            <td class="font-semibold text-ink">
              {{ item.thietBi?.tenThietBi || "-" }}
            </td>
            <td>{{ item.thietBi?.maThietBi || "-" }}</td>
            <td>{{ item.thietBi?.khoaPhongHienTai || "-" }}</td>
            <td>{{ formatDate(item.thoiGianBaoDuong) }}</td>
            <td>{{ formatDate(item.thoiGianBaoDuongTiepTheo) }}</td>
            <td>{{ formatCurrency(item.soTien) }}</td>
            <td>
              <span :class="statusClass(item.trangThai)">
                {{ item.trangThai ? "Hoàn thành" : "Chưa hoàn thành" }}
              </span>
            </td>
            <td>{{ item.ghiChu || "-" }}</td>
            <td class="text-right">
              <UiButton
                variant="secondary"
                size="sm"
                icon="edit"
                @click="openUpdateModal(item)"
              >
                Cập nhật
              </UiButton>
            </td>
          </tr>
          <tr v-if="!filteredRows.length && !loading">
            <td colspan="10" class="py-10 text-center text-slate-500">
              Không có dữ liệu bảo dưỡng phù hợp.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <UiPagination
      v-model="page"
      :total="filteredRows.length"
      :page-size="pageSize"
    />

    <UiModal v-if="showMaintenanceModal">
      <form @submit.prevent="submitMaintenance">
        <div class="mb-4 flex items-start justify-between gap-3">
          <div>
            <p class="text-xl font-bold text-ink">
              {{ maintenanceModalTitle }}
            </p>
          </div>
          <UiButton
            variant="ghost"
            size="sm"
            icon="close"
            @click="closeMaintenanceModal"
          >
            Đóng
          </UiButton>
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          <UiSelect
            v-if="maintenanceMode === 'create'"
            v-model.number="maintenanceForm.idThietBi"
            class="md:col-span-2"
            label="Thiết bị"
            required
          >
            <option :value="0">Chọn thiết bị</option>
            <option v-for="item in devices" :key="item.id" :value="item.id">
              {{ item.tenThietBi }} - {{ item.maThietBi || "Chưa có mã" }}
            </option>
          </UiSelect>
          <UiInput
            v-else
            :model-value="maintenanceDeviceLabel"
            class="md:col-span-2"
            label="Thiết bị"
            disabled
          />
          <UiDatePicker
            v-model="maintenanceForm.thoiGianBaoDuong"
            label="Thời gian bảo dưỡng"
            required
          />
          <UiDatePicker
            v-model="maintenanceForm.thoiGianBaoDuongTiepTheo"
            label="Bảo dưỡng tiếp theo"
            required
          />
          <UiSelect
            v-if="maintenanceMode === 'update'"
            v-model="maintenanceForm.trangThai"
            label="Trạng thái"
            required
          >
            <option :value="false">Chưa hoàn thành</option>
            <option :value="true">Hoàn thành</option>
          </UiSelect>
          <UiInput
            :model-value="maintenanceCostInput"
            :class="maintenanceMode === 'update' ? '' : 'md:col-span-2'"
            label="Số tiền"
            inputmode="numeric"
            required
            @keydown="blockNonNumericKey"
            @paste="pasteNumericOnly($event, updateMaintenanceCost)"
            @update:modelValue="updateMaintenanceCost"
          />
          <UiTextarea
            v-model="maintenanceForm.ghiChu"
            class="md:col-span-2"
            label="Ghi chú"
            placeholder="Nhập ghi chú bảo dưỡng"
          />
        </div>

        <div class="mt-4 flex justify-end gap-3">
          <UiButton
            type="button"
            variant="secondary"
            icon="cancel"
            @click="closeMaintenanceModal"
          >
            Hủy
          </UiButton>
          <UiButton
            type="submit"
            icon="confirm"
            :loading="saving"
            :disabled="isMaintenanceDisabled"
          >
            Xác nhận
          </UiButton>
        </div>
      </form>
    </UiModal>

    <UiModal v-if="showConfigModal">
      <form @submit.prevent="submitConfig">
        <div class="mb-4 flex items-start justify-between gap-3">
          <div>
            <p class="text-xl font-bold text-ink">{{ configModalTitle }}</p>
            <p class="text-sm text-slate-500">
              Nhập thời gian bảo dưỡng mặc định
            </p>
          </div>
          <UiButton
            variant="ghost"
            size="sm"
            icon="close"
            @click="closeConfigModal"
          >
            Đóng
          </UiButton>
        </div>

        <UiInput
          v-model.number="configForm.thoiGianBaoDuong"
          label="Thời gian bảo dưỡng"
          type="number"
          min="0"
          required
        />

        <div class="mt-4 flex justify-end gap-3">
          <UiButton
            type="button"
            variant="secondary"
            icon="cancel"
            @click="closeConfigModal"
          >
            Hủy
          </UiButton>
          <UiButton
            type="submit"
            icon="confirm"
            :loading="configSaving"
            :disabled="configSaving"
          >
            Xác nhận
          </UiButton>
        </div>
      </form>
    </UiModal>
  </section>
</template>

<script lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import QLTSRepository from "@/services/QLTSRepository";
import { ThietBi } from "@/models/thietBi";
import {
  blockNonNumericKey,
  formatCurrency,
  formatDate,
  formatIntegerInput,
  normalizeText,
  parseIntegerInput,
  pasteNumericOnly,
} from "@/utils/format";
import { getErrorMessage, useToast } from "@/utils/toast";

const toInputDate = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;
};

type BaoDuong = {
  id: number;
  idThietBi: number;
  thietBi?: {
    tenThietBi?: string;
    maThietBi?: string;
    khoaPhongHienTai?: string;
  };
  thoiGianBaoDuong?: string;
  thoiGianBaoDuongTiepTheo?: string;
  trangThai?: boolean;
  ghiChu?: string;
  soTien?: number;
};

type CauHinhBaoDuong = {
  thoiGianBaoDuong: number;
  id: number;
};

export default {
  setup() {
    const store = useStore();
    const { showToast } = useToast();
    const rows = ref<BaoDuong[]>([]);
    const loading = ref(false);
    const query = ref("");
    const page = ref(1);
    const pageSize = 10;
    const devices = ref<ThietBi[]>([]);
    const showMaintenanceModal = ref(false);
    const saving = ref(false);
    const configRows = ref<CauHinhBaoDuong[]>([]);
    const configLoading = ref(false);
    const configSaving = ref(false);
    const showConfigModal = ref(false);
    const configMode = ref<"create" | "update">("create");
    const configForm = ref({
      thoiGianBaoDuong: 0,
    });
    const maintenanceMode = ref<"create" | "update">("create");
    const maintenanceForm = ref({
      id: 0,
      idThietBi: 0,
      tenThietBi: "",
      maThietBi: "",
      thoiGianBaoDuong: "",
      thoiGianBaoDuongTiepTheo: "",
      trangThai: false,
      ghiChu: "",
      soTien: 0,
    });

    const canManageConfig = computed(() => store.state.permission === "admin");
    const currentConfig = computed(() => configRows.value[0] || null);
    const configModalTitle = computed(() =>
      configMode.value === "update" ? "Cập nhật cấu hình" : "Tạo cấu hình",
    );
    const maintenanceModalTitle = computed(() =>
      maintenanceMode.value === "update"
        ? "Cập nhật lịch sử bảo dưỡng"
        : "Tạo lịch bảo dưỡng",
    );
    const maintenanceCostInput = computed(() =>
      formatIntegerInput(maintenanceForm.value.soTien),
    );
    const maintenanceDeviceLabel = computed(() => {
      const name = maintenanceForm.value.tenThietBi;
      const code = maintenanceForm.value.maThietBi;
      if (name && code) return `${name} - ${code}`;
      if (name) return name;

      const device = devices.value.find(
        (item) => item.id === maintenanceForm.value.idThietBi,
      );
      if (!device) return "-";
      return `${device.tenThietBi} - ${device.maThietBi || "Chưa có mã"}`;
    });
    const isMaintenanceDisabled = computed(
      () =>
        !maintenanceForm.value.idThietBi ||
        !maintenanceForm.value.thoiGianBaoDuong ||
        !maintenanceForm.value.thoiGianBaoDuongTiepTheo ||
        saving.value,
    );

    const toIsoDateTime = (value: string) =>
      value ? new Date(`${value}T00:00:00`).toISOString() : "";

    const loadRows = () => {
      loading.value = true;
      QLTSRepository.getListBaoDuong()
        .then((res) => {
          rows.value = Array.isArray(res.data) ? res.data : [];
        })
        .catch((err) => {
          rows.value = [];
          showToast(
            getErrorMessage(err, "Không tải được lịch sử bảo dưỡng."),
            "error",
          );
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const loadDevices = () => {
      QLTSRepository.getListThietBi()
        .then((res) => {
          devices.value = Array.isArray(res.data) ? res.data : [];
        })
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không tải được danh sách thiết bị."),
            "error",
          );
        });
    };

    const loadConfig = () => {
      if (!canManageConfig.value) return;
      configLoading.value = true;
      QLTSRepository.getCauHinh()
        .then((res) => {
          configRows.value = Array.isArray(res.data)
            ? res.data
            : res.data
              ? [res.data]
              : [];
        })
        .catch((err) => {
          configRows.value = [];
          showToast(
            getErrorMessage(
              err,
              "Không tải được cấu hình bảo trì bảo dưỡng.",
            ),
            "error",
          );
        })
        .finally(() => {
          configLoading.value = false;
        });
    };

    const openCreateConfigModal = () => {
      configMode.value = "create";
      configForm.value = {
        thoiGianBaoDuong: 0,
      };
      showConfigModal.value = true;
    };

    const openUpdateConfigModal = () => {
      if (!currentConfig.value) return;
      configMode.value = "update";
      configForm.value = {
        thoiGianBaoDuong: Number(currentConfig.value.thoiGianBaoDuong || 0),
      };
      showConfigModal.value = true;
    };

    const closeConfigModal = () => {
      if (configSaving.value) return;
      showConfigModal.value = false;
    };

    const submitConfig = () => {
      configSaving.value = true;
      const payload = {
        thoiGianBaoDuong: Number(configForm.value.thoiGianBaoDuong || 0),
      };
      const request =
        configMode.value === "update"
          ? QLTSRepository.updateCauHinh(payload)
          : QLTSRepository.createCauHinh(payload);

      request
        .then(() => {
          showToast(
            configMode.value === "update"
              ? "Đã cập nhật cấu hình bảo trì bảo dưỡng."
              : "Đã tạo cấu hình bảo trì bảo dưỡng.",
            "success",
          );
          showConfigModal.value = false;
          loadConfig();
        })
        .catch((err) => {
          showToast(
            getErrorMessage(
              err,
              configMode.value === "update"
                ? "Không cập nhật được cấu hình bảo trì bảo dưỡng."
                : "Không tạo được cấu hình bảo trì bảo dưỡng.",
            ),
            "error",
          );
        })
        .finally(() => {
          configSaving.value = false;
        });
    };

    const openCreateModal = () => {
      maintenanceMode.value = "create";
      maintenanceForm.value = {
        id: 0,
        idThietBi: 0,
        tenThietBi: "",
        maThietBi: "",
        thoiGianBaoDuong: "",
        thoiGianBaoDuongTiepTheo: "",
        trangThai: false,
        ghiChu: "",
        soTien: 0,
      };
      showMaintenanceModal.value = true;
      if (!devices.value.length) {
        loadDevices();
      }
    };

    const openUpdateModal = (item: BaoDuong) => {
      maintenanceMode.value = "update";
      maintenanceForm.value = {
        id: item.id,
        idThietBi: item.idThietBi,
        tenThietBi: item.thietBi?.tenThietBi || "",
        maThietBi: item.thietBi?.maThietBi || "",
        thoiGianBaoDuong: toInputDate(item.thoiGianBaoDuong),
        thoiGianBaoDuongTiepTheo: toInputDate(item.thoiGianBaoDuongTiepTheo),
        trangThai: Boolean(item.trangThai),
        ghiChu: item.ghiChu || "",
        soTien: Number(item.soTien || 0),
      };
      showMaintenanceModal.value = true;
      if (!devices.value.length) {
        loadDevices();
      }
    };

    const closeMaintenanceModal = () => {
      if (saving.value) return;
      showMaintenanceModal.value = false;
    };

    const updateMaintenanceCost = (value: string) => {
      maintenanceForm.value.soTien = parseIntegerInput(value);
    };

    const submitMaintenance = () => {
      if (isMaintenanceDisabled.value) {
        showToast("Vui lòng nhập đầy đủ thông tin bảo dưỡng.", "error");
        return;
      }

      saving.value = true;
      const payload = {
        idThietBi: maintenanceForm.value.idThietBi,
        thoiGianBaoDuong: toIsoDateTime(maintenanceForm.value.thoiGianBaoDuong),
        thoiGianBaoDuongTiepTheo: toIsoDateTime(
          maintenanceForm.value.thoiGianBaoDuongTiepTheo,
        ),
        ghiChu: maintenanceForm.value.ghiChu.trim(),
        soTien: Number(maintenanceForm.value.soTien || 0),
      };
      const request =
        maintenanceMode.value === "update"
          ? QLTSRepository.updateLichSuBaoDuong({
              ...payload,
              id: maintenanceForm.value.id,
              trangThai: maintenanceForm.value.trangThai,
            })
          : QLTSRepository.createLichSuBaoDuong(payload);

      request
        .then(() => {
          showToast(
            maintenanceMode.value === "update"
              ? "Đã cập nhật lịch sử bảo dưỡng."
              : "Đã tạo lịch sử bảo dưỡng.",
            "success",
          );
          showMaintenanceModal.value = false;
          loadRows();
        })
        .catch((err) => {
          showToast(
            getErrorMessage(
              err,
              maintenanceMode.value === "update"
                ? "Không cập nhật được lịch sử bảo dưỡng."
                : "Không tạo được lịch sử bảo dưỡng.",
            ),
            "error",
          );
        })
        .finally(() => {
          saving.value = false;
        });
    };

    const filteredRows = computed(() => {
      const term = normalizeText(query.value);
      if (!term) return rows.value;
      return rows.value.filter((item) =>
        [
          item.thietBi?.tenThietBi,
          item.thietBi?.maThietBi,
          item.thietBi?.khoaPhongHienTai,
          item.ghiChu,
          item.soTien,
        ]
          .map(normalizeText)
          .some((value) => value.includes(term)),
      );
    });

    const paginatedRows = computed(() => {
      const start = (page.value - 1) * pageSize;
      return filteredRows.value.slice(start, start + pageSize);
    });

    const rowIndex = (index: number) => (page.value - 1) * pageSize + index + 1;
    const statusClass = (status?: boolean) => [
      "status-pill",
      status ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700",
    ];

    watch(query, () => {
      page.value = 1;
    });
    watch(filteredRows, () => {
      const maxPage = Math.max(
        1,
        Math.ceil(filteredRows.value.length / pageSize),
      );
      if (page.value > maxPage) page.value = maxPage;
    });

    onMounted(() => {
      loadRows();
      loadDevices();
      loadConfig();
    });

    return {
      blockNonNumericKey,
      canManageConfig,
      closeConfigModal,
      closeMaintenanceModal,
      configForm,
      configLoading,
      configModalTitle,
      configSaving,
      currentConfig,
      devices,
      filteredRows,
      formatCurrency,
      formatDate,
      isMaintenanceDisabled,
      loadRows,
      loading,
      maintenanceCostInput,
      maintenanceDeviceLabel,
      maintenanceForm,
      maintenanceModalTitle,
      maintenanceMode,
      openCreateConfigModal,
      openCreateModal,
      openUpdateConfigModal,
      openUpdateModal,
      page,
      pageSize,
      paginatedRows,
      pasteNumericOnly,
      query,
      rowIndex,
      saving,
      showConfigModal,
      showMaintenanceModal,
      statusClass,
      submitConfig,
      submitMaintenance,
      updateMaintenanceCost,
    };
  },
};
</script>
